import React, { useState, useEffect, useRef } from 'react';
import '@/features/deployments/styles/DeploymentMonitoringDashboard.css';
import {
    Layout,
    Card,
    Select,
    DatePicker,
    Statistic,
    Typography,
    Tooltip,
    Badge,
    Space,
    ConfigProvider,
    theme,
    Button,
    Radio,
    Tag
} from 'antd';
import { sparkToast } from '@spark';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    Cell
} from 'recharts';
import {
    QuestionCircleOutlined,
    ClockCircleOutlined,
    BarChartOutlined,
    PieChartOutlined,
    LineChartOutlined,
    TableOutlined,
    InfoCircleOutlined,
    AreaChartOutlined,
    DatabaseOutlined,
    FilterOutlined,
    RiseOutlined,
    SearchOutlined,
    ClearOutlined
} from '@ant-design/icons';
import { Text as SparkText } from '@spark';
import RawDataTable from '@/features/tasks/components/RawDataTable';
import { data_from_server_get, data_from_server_post } from '@/shared/http/client';
import { getPredictionColor, predictionColors } from '@/shared/utils/helpers';
const { Content } = Layout;
const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { darkAlgorithm } = theme;

// Client ID - Would normally come from authentication context
const CLIENT_ID = "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75";

// Define a more harmonious and meaningful color palette
const dashboardColors = {
    success: '#52c41a',        // Bright green for success
    error: '#ff4d4f',          // Red for errors/failures
    warning: '#faad14',        // Warning amber
    info: '#1890ff',           // Info blue
    purple: '#722ed1',         // Purple for response time
    cyan: '#13c2c2',           // Cyan for secondary metrics
    lime: '#a0d911',           // Lime green for positive trends
    gold: '#faad14',           // Gold for important KPIs
    magenta: '#eb2f96',        // Magenta for highlighting
    // Chart series colors - visually distinguishable
    series: ['#1890ff', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96', '#13c2c2', '#fa541c', '#a0d911']
};

// CustomEmptyState component after dashboardColors
const CustomEmptyState = ({
    icon,
    title,
    description,
    height = 300,
    suggestion = null,
    action = null
}) => {
    return (
        <div
            style={{
                height: `${height}px`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '24px',
                textAlign: 'center',
                background: 'rgba(0, 0, 0, 0.15)',
                borderRadius: '8px',
                border: '1px dashed rgba(255, 255, 255, 0.15)',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(45deg, rgba(0,0,0,0.1) 25%, rgba(20,20,20,0.1) 50%, rgba(0,0,0,0.1) 75%)',
                backgroundSize: '400% 400%',
                animation: 'shimmer 3s ease infinite',
                zIndex: 0
            }} />

            <div style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                maxWidth: '320px'
            }}>
                <div style={{
                    fontSize: '64px',
                    color: 'rgba(255, 255, 255, 0.15)',
                    marginBottom: '8px',
                    animation: 'pulse 2s infinite ease-in-out'
                }}>
                    {icon}
                </div>
                <Typography.Title level={4} style={{ margin: '0 0 4px', color: 'rgba(255, 255, 255, 0.85)' }}>
                    {title}
                </Typography.Title>
                <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                    {description}
                </Typography.Text>

                {suggestion && (
                    <div style={{
                        marginTop: '16px',
                        padding: '8px 16px',
                        background: 'rgba(24, 144, 255, 0.1)',
                        borderRadius: '4px',
                        border: '1px solid rgba(24, 144, 255, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <InfoCircleOutlined style={{ color: dashboardColors.info }} />
                        <Typography.Text style={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '13px' }}>
                            {suggestion}
                        </Typography.Text>
                    </div>
                )}

                {action && (
                    <Button
                        type="primary"
                        ghost
                        icon={<FilterOutlined />}
                        style={{ marginTop: '16px' }}
                        onClick={action.onClick}
                    >
                        {action.label}
                    </Button>
                )}
            </div>
        </div>
    );
};

// Create a better presentation for empty statistic values
const StatisticPlaceholder = ({ color = dashboardColors.info, icon, text = "No data" }) => (
    <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
    }}>
        {icon && (
            <div style={{ fontSize: '28px', color: 'rgba(255, 255, 255, 0.15)', marginBottom: '8px' }}>
                {icon}
            </div>
        )}
        <div style={{
            fontSize: '28px',
            color,
            fontWeight: '500',
            letterSpacing: '0.5px',
            background: 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s infinite linear',
            padding: '4px 16px',
            borderRadius: '4px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
        }}>
            {text}
        </div>
    </div>
);

// Add a reusable EmptyDataMessage component after the StatisticPlaceholder component
const EmptyDataMessage = ({ icon, message, height = '300px' }) => (
    <div style={{
        height: "100%",
        minHeight: "300px",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        position: 'relative',
        width: '100%',
        margin: '0',
        padding: '0',
        overflow: 'hidden'
    }}>
        <div style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            right: '8px',
            bottom: '8px',
            background: 'rgba(0, 0, 0, 0.15)',
            borderRadius: '4px',
            border: '1px dashed rgba(255, 255, 255, 0.15)',
            zIndex: '0'
        }}></div>
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '1',
            width: '100%',
            height: '100%'
        }}>
            {React.cloneElement(icon, {
                style: {
                    fontSize: '48px',
                    color: 'rgba(255, 255, 255, 0.1)',
                    marginBottom: '16px'
                }
            })}
            <Typography.Text type="secondary" style={{ fontSize: '16px' }}>
                {message}
            </Typography.Text>
        </div>
    </div>
);

const ProductionMonitoring = () => {
    // Add ref for RawDataTable
    const rawDataTableRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [selectedApp, setSelectedApp] = useState('All Projects');
    const [selectedUsecase, setSelectedUsecase] = useState('All Tasks');
    const [dateRange, setDateRange] = useState(null);
    const [data, setData] = useState(null);
    const [applications, setApplications] = useState([]);
    const [usecases, setUsecases] = useState([]);
    const [compareModels, setCompareModels] = useState(true);
    const [selectedMetric, setSelectedMetric] = useState('avg');
    const [displayNames, setDisplayNames] = useState({});
    // Add pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [tableFilters, setTableFilters] = useState({});

    // API helper functions

    const fetchApplications = () => {
        try {
            data_from_server_get('deployment-monitoring/applications',
                (response) => {
                    setApplications(response.applications);
                }
            );
        } catch (error) {
            console.error('Error fetching projects:', error);
            sparkToast.error('Failed to load projects');
            return [];
        }
    };


    const fetchUsecases = () => {
        try {
            data_from_server_get('deployment-monitoring/usecases',
                (response) => {
                    setUsecases(response.usecases);
                }
            );
        } catch (error) {
            console.error('Error fetching usecases:', error);
            sparkToast.error('Failed to load usecases');
            return [];
        }
    };

    // Implement the checkForComparableData function above fetchMetrics
    const checkForComparableData = (data) => {
        if (!data || !data.raw_requests || data.raw_requests.length === 0) {
            return false;
        }

        // Analyze raw request data to determine if there's both optune and commercial data
        const usecaseModels = {};

        data.raw_requests.forEach(record => {
            const usecase = record.usecase;
            const isOptune = record.isFromOptuneModel;

            if (!usecaseModels[usecase]) {
                usecaseModels[usecase] = { hasOptune: false, hasCommercial: false };
            }

            if (isOptune) {
                usecaseModels[usecase].hasOptune = true;
            } else {
                usecaseModels[usecase].hasCommercial = true;
            }
        });

        // Check if any usecase has both types of models
        return Object.values(usecaseModels).some(
            models => models.hasOptune && models.hasCommercial
        );
    };

    // Update fetchMetrics to include pagination parameters
    const fetchMetrics = (filters = null) => {
        setLoading(true);

        // Create a default filters object
        const defaultFilters = {
            application: "All Projects",
            usecase: "All Tasks",
            date_range: null,
            page: 1,
            page_size: 10
        };

        // If filters is provided, use it, otherwise use defaults
        const finalFilters = filters || defaultFilters;

        const requestBody = {
            client_id: CLIENT_ID,
            filters: finalFilters
        };

        try {
            data_from_server_post('deployment-monitoring/metrics',
                requestBody,
                (response) => {
                    if (!response || response.error) {
                        console.error('API returned error:', response?.error || 'No data');
                        sparkToast.error('Failed to load data: ' + (response?.error || 'Unknown error'));
                        setLoading(false);
                        return;
                    }

                    setData(response);

                    // Store total records count for pagination
                    if (response.total_records !== undefined) {
                        setTotalRecords(response.total_records);
                    }

                    // If filters provided page information, update state to match
                    if (finalFilters.page) {
                        setCurrentPage(finalFilters.page);
                    }
                    if (finalFilters.page_size) {
                        setPageSize(finalFilters.page_size);
                    }

                    setLoading(false);
                }
            );
        } catch (error) {
            console.error('Error fetching metrics:', error);
            sparkToast.error('Failed to load metrics: ' + error.message);
            setLoading(false);
        }
    };

    // Initial data load
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);

            try {
                // Load initial data
                fetchApplications();
                fetchUsecases();
                fetchMetrics(null);

                // If a specific usecase is selected, fetch its display names
                if (selectedUsecase !== "All Tasks") {
                    fetchDisplayNames(selectedUsecase);
                }
            } catch (error) {
                console.error('Error loading initial data:', error);
                sparkToast.error('Failed to load dashboard data');
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, []);

    // Add useEffect to fetch display names when usecase changes
    useEffect(() => {
        if (selectedUsecase !== "All Tasks") {
            fetchDisplayNames(selectedUsecase);
        }
    }, [selectedUsecase]);

    // Fetch filtered data when filters change
    useEffect(() => {
        const loadFilteredData = async () => {
            setLoading(true);

            // Always reset to page 1 when filters change
            setCurrentPage(1);

            // Always provide a filters object, even if some fields are default values
            const filters = {
                application: selectedApp !== "All Projects" ? selectedApp : "All Projects",
                usecase: selectedUsecase !== "All Tasks" ? selectedUsecase : "All Tasks",
                date_range: dateRange ? [
                    dateRange[0].format('YYYY-MM-DD'),
                    dateRange[1].format('YYYY-MM-DD')
                ] : null,
                // Important: Explicitly set to page 1 here to ensure consistency
                page: 1,
                page_size: pageSize,
                // Include any advanced filters
                ...tableFilters
            };

            try {
                // Fetch filtered metrics
                fetchMetrics(filters);
            } catch (error) {
                console.error('Error loading filtered data:', error);
                sparkToast.error('Failed to load filtered data');
            } finally {
                setLoading(false);
            }
        };

        // Load filtered data on initial load and when filters change
        loadFilteredData();
    }, [selectedApp, selectedUsecase, dateRange, tableFilters, pageSize]);

    // Check for comparable data when data changes
    useEffect(() => {
        if (data && data.raw_requests) {
            // Use our checkForComparableData function for consistency
            const hasComparable = checkForComparableData(data);
            setCompareModels(hasComparable);
        }
    }, [data]);

    const handleAppChange = (value) => {
        setSelectedApp(value);
        // When changing application, we should reset the usecase if necessary
        if (value !== selectedApp) {
            setSelectedUsecase('All Tasks');
            // Also clear any advanced filters
            setTableFilters({});
        }
    };

    const handleUsecaseChange = (value) => {
        setSelectedUsecase(value);
        // Clear any advanced filters when usecase changes
        if (value !== selectedUsecase) {
            setTableFilters({});
        }
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        // Clear advanced filters when date range changes
        if (dates !== dateRange) {
            setTableFilters({});
        }
    };

    // Add fetchDisplayNames function after the existing API helper functions
    const fetchDisplayNames = (usecase) => {
        if (!usecase || usecase === "All Tasks") return;

        data_from_server_post(
            'get-display-names-for-usecase',
            {
                client_id: CLIENT_ID,
                usecase_name: usecase,
            },
            (response) => {

                setDisplayNames(prevNames => ({
                    ...prevNames,
                    [usecase]: response || {}
                }));
            }
        );
    };

    const renderUsecaseRequestsCard = () => {

        return (
            <Card
                title={
                    <Space>
                        <BarChartOutlined />
                        <span>Requests Per Task</span>
                        <Tooltip title="Total number of inference requests per task">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Space>
                }
                className="dashboard-card"
                style={{
                    height: "100%",
                }}
            >
                {!data ? (
                    <CustomEmptyState
                        icon={<BarChartOutlined />}
                        title="No Request Data Available"
                        description="We're not seeing any requests for the selected filters."
                        suggestion="Try selecting a different application or date range to see the distribution of requests across usecases."
                        action={{
                            label: "Adjust Filters",
                            onClick: () => document.querySelector('.ant-select').focus()
                        }}
                    />
                ) : (!data.requests_per_usecase ||
                    data.requests_per_usecase.length === 0 ||
                    data.requests_per_usecase.every(item => item.requests === 0)) ? (
                    <EmptyDataMessage
                        icon={<BarChartOutlined />}
                        message="No request data for current selection"
                    />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ flex: '1' }}>
                            <Statistic
                                title="Total Requests per Task"
                                value={data?.requests_per_usecase?.reduce((sum, item) => sum + item.requests, 0) || 0}
                                groupSeparator=","
                            />
                        </div>
                        <div style={{ flex: '1' }}>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={data?.requests_per_usecase}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                                    <XAxis dataKey="usecase" />
                                    <YAxis />
                                    <RechartsTooltip
                                        formatter={(value) => [`${value.toLocaleString()} requests`, 'Count']}
                                        contentStyle={{ backgroundColor: '#2a2a2a', border: 'none' }}
                                    />
                                    <Bar dataKey="requests" fill={predictionColors[0]} name="Requests">
                                        {data?.requests_per_usecase?.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={predictionColors[index % predictionColors.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </Card>
        );
    };

    const renderDailyRequestsCard = () => {
        // Determine which usecases have data in the current dataset
        const getUsecasesWithData = () => {
            if (!data?.daily_requests || data.daily_requests.length === 0) return [];

            // If a specific usecase is selected, only show that one
            if (selectedUsecase !== "All Tasks") {
                return [selectedUsecase];
            }

            // Otherwise, find all usecases with any data across ALL dates
            const availableUsecases = new Set();

            // First identify all possible usecases from all days
            data.daily_requests.forEach(dayData => {
                Object.keys(dayData).forEach(key => {
                    // Include all keys except 'date', even if they have zero values
                    if (key !== 'date') {
                        availableUsecases.add(key);
                    }
                });
            });

            return Array.from(availableUsecases);
        };

        // Get usecases with data
        const usecasesWithData = getUsecasesWithData();

        // Create a consistent color mapping for usecases
        const usecaseColors = {};
        usecasesWithData.forEach((usecase, index) => {
            usecaseColors[usecase] = predictionColors[index % predictionColors.length];
        });

        // Create a title that includes the date range if selected
        const getTimeRangeTitle = () => {
            if (dateRange) {
                return (
                    <Space>
                        <LineChartOutlined />
                        <span>Requests Per Day</span>
                        <span style={{
                            fontSize: '12px',
                            background: 'rgba(24, 144, 255, 0.1)',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            marginLeft: '8px',
                            color: dashboardColors.info,
                            border: `1px solid ${dashboardColors.info}30`
                        }}>
                            {`${dateRange[0].format('MMM D')} - ${dateRange[1].format('MMM D, YYYY')}`}
                        </span>
                        <Tooltip title="Daily distribution of requests across different tasks">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Space>
                );
            } else {
                return (
                    <Space>
                        <LineChartOutlined />
                        <span>Requests Per Day</span>
                        <span style={{
                            fontSize: '12px',
                            background: 'rgba(24, 144, 255, 0.08)',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            marginLeft: '8px',
                            color: 'rgba(0, 0, 0, 0.65)',
                            border: '1px solid rgba(0, 0, 0, 0.1)'
                        }}>
                            All Time
                        </span>
                        <Tooltip title="Daily distribution of requests across different tasks">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Space>
                );
            }
        };

        return (
            <Card
                title={getTimeRangeTitle()}
                className="dashboard-card"
            >
                {!data ? (
                    <CustomEmptyState
                        icon={<AreaChartOutlined />}
                        title="Daily Trend Data Not Available"
                        description="Time-based analytics requires date selection to show request patterns."
                        suggestion="Use the date picker above to select a specific time period for analysis."
                    />
                ) : (!data.daily_requests || data.daily_requests.length === 0) ? (
                    <EmptyDataMessage
                        icon={<AreaChartOutlined />}
                        message="No daily request data for current selection"
                    />
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart
                            data={data?.daily_requests}
                            margin={{ top: 20, right: 50, left: 20, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                            <XAxis
                                dataKey="date"
                                padding={{ left: 10, right: 10 }}
                                tickFormatter={(value) => value}
                                // Always show all ticks instead of just start and end
                                interval={0}
                                tick={{ fontSize: 12 }}
                                // Ensure all dates are shown even with minimal data
                                minTickGap={0}
                                allowDataOverflow={false}
                                allowDecimals={false}
                            />
                            <YAxis />
                            <RechartsTooltip
                                contentStyle={{ backgroundColor: '#2a2a2a', border: 'none' }}
                                // Show all items in tooltip, even those with value 0
                                filterNull={false}
                            />
                            <Legend />
                            {usecasesWithData.map((usecase) => {
                                // Ensure each usecase has its own consistent color
                                const color = usecaseColors[usecase];
                                return (
                                    <Line
                                        key={usecase}
                                        type="monotone"
                                        dataKey={usecase}
                                        name={usecase}
                                        stroke={color}
                                        fill={color}
                                        activeDot={{ r: 8 }}
                                        strokeWidth={2}
                                        // Connect null data points to ensure line continuity
                                        connectNulls={false}
                                        // Make sure zero values are treated as zero, not null
                                        isAnimationActive={true}
                                        // Ensure all points are visible, even if they have 0 value
                                        dot={(props) => {
                                            const { cx, cy, payload, dataKey } = props;
                                            // Create a unique key using dataKey and payload values
                                            const key = `${dataKey}-${payload.date}-${cx}-${cy}`;
                                            return (
                                                <circle
                                                    key={key}
                                                    cx={cx}
                                                    cy={cy}
                                                    r={4}
                                                    stroke={color}
                                                    strokeWidth={1}
                                                    fill={color}
                                                />
                                            );
                                        }}
                                    />
                                );
                            })}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Card>
        );
    };

    const renderSuccessFailureCard = () => {
        return (
            <Card
                title={
                    <Space>
                        <PieChartOutlined />
                        <span>Success vs. Failure</span>
                        <Tooltip title="Overall success and failure rates for model requests">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Space>
                }
                className="dashboard-card"
            >
                {!data ? (
                    <CustomEmptyState
                        icon={<PieChartOutlined />}
                        title="Success Rate Analysis"
                        description="No success/failure metrics are available for the selected filters."
                        suggestion="Select a specific application to view its success and error rates."
                        height={380}
                    />
                ) : (!data.success_failure || data.success_failure.length === 0 ||
                    (data.success_failure.reduce((sum, item) => sum + item.success, 0) === 0 &&
                        data.success_failure.reduce((sum, item) => sum + item.failure, 0) === 0)) ? (
                    <EmptyDataMessage
                        icon={<PieChartOutlined />}
                        message="No success/failure data for current selection"
                        height="100%"
                    />
                ) : (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '24px'
                    }}>
                        <div style={{
                            flex: '0 1 170px',
                            minWidth: '0',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <div style={{ textAlign: 'left', padding: '0 16px' }}>
                                <Statistic
                                    title="Success Rate"
                                    value={(() => {
                                        const totalSuccess = data?.success_failure?.reduce((sum, item) => sum + item.success, 0) || 0;
                                        const totalFailure = data?.success_failure?.reduce((sum, item) => sum + item.failure, 0) || 0;
                                        const totalRequests = totalSuccess + totalFailure;
                                        return totalRequests > 0 ? ((totalSuccess / totalRequests) * 100).toFixed(2) : '0.00';
                                    })()}
                                    suffix="%"
                                    valueStyle={{
                                        color: dashboardColors.success,
                                        fontSize: '32px',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap'
                                    }}
                                />
                                <div style={{ marginTop: 24 }}>
                                    <Badge
                                        status="success"
                                        text={
                                            <span style={{ fontSize: '16px', color: dashboardColors.success }}>
                                                {`Success: ${(data?.success_failure?.reduce((sum, item) => sum + item.success, 0) || 0).toLocaleString()}`}
                                            </span>
                                        }
                                        style={{ display: 'block' }}
                                    />
                                    <Badge
                                        status="error"
                                        text={
                                            <span style={{ fontSize: '16px', color: dashboardColors.error }}>
                                                {`Failure: ${(data?.success_failure?.reduce((sum, item) => sum + item.failure, 0) || 0).toLocaleString()}`}
                                            </span>
                                        }
                                        style={{ display: 'block', marginTop: 12 }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{
                            flex: '1 1 400px',
                            minWidth: '0',
                            height: '380px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative'
                        }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={[
                                            {
                                                name: 'Success',
                                                value: data?.success_failure.reduce((sum, item) => sum + item.success, 0) || 0
                                            },
                                            {
                                                name: 'Failure',
                                                value: data?.success_failure?.reduce((sum, item) => sum + item.failure, 0) || 0
                                            }
                                        ]}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={80}
                                        outerRadius={120}
                                        fill="#8884d8"
                                        paddingAngle={5}
                                        dataKey="value"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {[0, 1].map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={index === 0 ? dashboardColors.success : dashboardColors.error}
                                                stroke={index === 0 ? "#388e3c" : "#c62828"}
                                                strokeWidth={1}
                                            />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip
                                        formatter={(value, name) => [`${value.toLocaleString()}`, name]}
                                        contentStyle={{
                                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                            border: '1px solid #666',
                                            borderRadius: '4px',
                                            padding: '10px',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                        }}
                                        itemStyle={{ color: '#ffffff' }}
                                        labelStyle={{ color: '#ffffff', marginBottom: '8px', fontWeight: 'bold' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </Card>
        );
    };

    const renderResponseTimeCard = () => {

        // Process response time data to separate SpecificAI vs Commercial model performance
        const processResponseTimeData = () => {
            if (!data?.raw_requests || data.raw_requests.length === 0) return null;

            // Group by usecase and model type (SpecificAI vs Commercial)
            const groupedData = {};

            data.raw_requests.forEach(record => {
                const usecase = record.usecase;
                const isOptune = record.isFromOptuneModel;
                const responseTime = record.responseTime || 0;

                if (!groupedData[usecase]) {
                    groupedData[usecase] = {
                        optune: { times: [], count: 0, total: 0 },
                        commercial: { times: [], count: 0, total: 0 }
                    };
                }

                const category = isOptune ? 'optune' : 'commercial';
                groupedData[usecase][category].times.push(responseTime);
                groupedData[usecase][category].count += 1;
                groupedData[usecase][category].total += responseTime;
            });

            // Calculate averages and improvements
            const result = [];
            for (const [usecase, data] of Object.entries(groupedData)) {
                // Calculate metrics for SpecificAI models
                const optuneTimes = data.optune.times.sort((a, b) => a - b);
                const optuneAvg = data.optune.count > 0 ? data.optune.total / data.optune.count : 0;
                const optuneMedian = data.optune.count > 0
                    ? (optuneTimes.length % 2 === 0)
                        ? (optuneTimes[optuneTimes.length / 2 - 1] + optuneTimes[optuneTimes.length / 2]) / 2
                        : optuneTimes[Math.floor(optuneTimes.length / 2)]
                    : 0;
                const optuneMin = optuneTimes.length > 0 ? optuneTimes[0] : 0;
                const optuneMax = optuneTimes.length > 0 ? optuneTimes[optuneTimes.length - 1] : 0;

                // Calculate metrics for commercial models
                const commercialTimes = data.commercial.times.sort((a, b) => a - b);
                const commercialAvg = data.commercial.count > 0 ? data.commercial.total / data.commercial.count : 0;
                const commercialMedian = data.commercial.count > 0
                    ? (commercialTimes.length % 2 === 0)
                        ? (commercialTimes[commercialTimes.length / 2 - 1] + commercialTimes[commercialTimes.length / 2]) / 2
                        : commercialTimes[Math.floor(commercialTimes.length / 2)]
                    : 0;
                const commercialMin = commercialTimes.length > 0 ? commercialTimes[0] : 0;
                const commercialMax = commercialTimes.length > 0 ? commercialTimes[commercialTimes.length - 1] : 0;

                // Calculate improvement percentage (based on average)
                let improvementPercent = 0;
                if (commercialAvg > 0 && optuneAvg > 0) {
                    improvementPercent = ((commercialAvg - optuneAvg) / commercialAvg) * 100;
                }

                // Calculate improvement for other metrics
                let medianImprovement = 0;
                if (commercialMedian > 0 && optuneMedian > 0) {
                    medianImprovement = ((commercialMedian - optuneMedian) / commercialMedian) * 100;
                }

                let minImprovement = 0;
                if (commercialMin > 0 && optuneMin > 0) {
                    minImprovement = ((commercialMin - optuneMin) / commercialMin) * 100;
                }

                let maxImprovement = 0;
                if (commercialMax > 0 && optuneMax > 0) {
                    maxImprovement = ((commercialMax - optuneMax) / commercialMax) * 100;
                }

                result.push({
                    usecase,
                    // Optune metrics
                    optuneAvg,
                    optuneMedian,
                    optuneMin,
                    optuneMax,
                    // Commercial metrics
                    commercialAvg,
                    commercialMedian,
                    commercialMin,
                    commercialMax,
                    // Improvement percentages
                    improvementPercent,
                    medianImprovement,
                    minImprovement,
                    maxImprovement,
                    // Meta information
                    hasOptune: data.optune.count > 0,
                    hasCommercial: data.commercial.count > 0,
                    optuneCount: data.optune.count,
                    commercialCount: data.commercial.count,
                    optuneTimes: data.optune.times,
                    commercialTimes: data.commercial.times
                });
            }

            return result;
        };

        const comparisonData = processResponseTimeData();

        // Create data for the chart based on comparison mode
        const getChartData = () => {
            if (!comparisonData) return [];

            if (compareModels) {
                // When comparing, create data points for side-by-side comparison based on selected metric
                return comparisonData
                    .map(item => {
                        // Get the improvement percentage for the selected metric
                        let improvement;
                        switch (selectedMetric) {
                            case 'median':
                                improvement = item.medianImprovement;
                                break;
                            case 'min':
                                improvement = item.minImprovement;
                                break;
                            case 'max':
                                improvement = item.maxImprovement;
                                break;
                            default: // 'avg'
                                improvement = item.improvementPercent;
                        }

                        // Return the data point with the correct metrics
                        return {
                            usecase: item.usecase,
                            optune: item[`optune${selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}`],
                            commercial: item[`commercial${selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}`],
                            improvement: improvement.toFixed(0),
                            metricType: selectedMetric
                        };
                    })
                    .sort((a, b) => {
                        // Sort by improvement percentage in descending order (same as improvement cards)
                        return parseFloat(b.improvement) - parseFloat(a.improvement);
                    });
            } else {
                // When not comparing, show all metrics together
                return data?.response_time || [];
            }
        };

        const chartData = getChartData();

        // Custom tooltip for the comparison chart
        const CustomTooltip = ({ active, payload, label }) => {
            if (active && payload && payload.length) {
                const usecaseData = comparisonData.find(item => item.usecase === label);
                if (!usecaseData) return null;

                // Get a human-readable name for the current metric
                const metricNames = {
                    avg: "Average",
                    median: "Median",
                    min: "Minimum",
                    max: "Maximum"
                };
                const currentMetricName = metricNames[selectedMetric] || "Average";

                return (
                    <div style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        border: '1px solid #444',
                        borderRadius: '4px',
                        padding: '10px 14px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        maxWidth: '320px'
                    }}>
                        <p style={{ color: '#fff', fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                            {label} <span style={{ fontWeight: 'normal', opacity: 0.7 }}>({currentMetricName} Response Time)</span>
                        </p>

                        {usecaseData.hasOptune && (
                            <div style={{ marginBottom: '12px' }}>
                                <p style={{
                                    color: dashboardColors.success,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    margin: '8px 0 6px',
                                    fontWeight: 'bold'
                                }}>
                                    <span style={{
                                        width: '12px',
                                        height: '12px',
                                        backgroundColor: dashboardColors.success,
                                        borderRadius: '50%',
                                        display: 'inline-block'
                                    }}></span>
                                    Optune Model <span style={{ fontSize: '12px', opacity: 0.8 }}>
                                        ({usecaseData.optuneCount} requests)
                                    </span>
                                </p>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr',
                                    gap: '4px 12px',
                                    fontSize: '13px',
                                    paddingLeft: '20px'
                                }}>
                                    {selectedMetric === 'avg' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Average:</span>
                                            <span style={{
                                                color: dashboardColors.success,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.optuneAvg.toFixed(2)} ms</span>
                                        </>
                                    )}

                                    {selectedMetric === 'median' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Median:</span>
                                            <span style={{
                                                color: dashboardColors.success,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.optuneMedian.toFixed(2)} ms</span>
                                        </>
                                    )}

                                    {selectedMetric === 'min' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Min:</span>
                                            <span style={{
                                                color: dashboardColors.success,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.optuneMin.toFixed(2)} ms</span>
                                        </>
                                    )}

                                    {selectedMetric === 'max' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Max:</span>
                                            <span style={{
                                                color: dashboardColors.success,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.optuneMax.toFixed(2)} ms</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {usecaseData.hasCommercial && (
                            <div style={{ marginBottom: '12px' }}>
                                <p style={{
                                    color: dashboardColors.info,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    margin: '8px 0 6px',
                                    fontWeight: 'bold'
                                }}>
                                    <span style={{
                                        width: '12px',
                                        height: '12px',
                                        backgroundColor: dashboardColors.info,
                                        borderRadius: '50%',
                                        display: 'inline-block'
                                    }}></span>
                                    Commercial Model <span style={{ fontSize: '12px', opacity: 0.8 }}>
                                        ({usecaseData.commercialCount} requests)
                                    </span>
                                </p>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr',
                                    gap: '4px 12px',
                                    fontSize: '13px',
                                    paddingLeft: '20px'
                                }}>
                                    {selectedMetric === 'avg' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Average:</span>
                                            <span style={{
                                                color: dashboardColors.info,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.commercialAvg.toFixed(2)} ms</span>
                                        </>
                                    )}

                                    {selectedMetric === 'median' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Median:</span>
                                            <span style={{
                                                color: dashboardColors.info,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.commercialMedian.toFixed(2)} ms</span>
                                        </>
                                    )}

                                    {selectedMetric === 'min' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Min:</span>
                                            <span style={{
                                                color: dashboardColors.info,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.commercialMin.toFixed(2)} ms</span>
                                        </>
                                    )}

                                    {selectedMetric === 'max' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Max:</span>
                                            <span style={{
                                                color: dashboardColors.info,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.commercialMax.toFixed(2)} ms</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {usecaseData.hasOptune && usecaseData.hasCommercial && (
                            <div style={{
                                marginTop: '8px',
                                padding: '8px 12px',
                                backgroundColor: 'rgba(82, 196, 26, 0.2)',
                                borderRadius: '4px',
                                border: '1px dashed rgba(82, 196, 26, 0.4)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    color: dashboardColors.success,
                                    fontWeight: 'bold',
                                    fontSize: '14px'
                                }}>
                                    <RiseOutlined />
                                    <span>Performance Improvement with Optune</span>
                                </div>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr',
                                    gap: '2px 12px',
                                    fontSize: '13px',
                                    marginTop: '2px',
                                    marginLeft: '20px'
                                }}>
                                    {selectedMetric === 'avg' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Average:</span>
                                            <span style={{
                                                color: dashboardColors.success,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.improvementPercent > 0 ? `${usecaseData.improvementPercent.toFixed(1)}% faster` : 'No improvement'}</span>
                                        </>
                                    )}

                                    {selectedMetric === 'median' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Median:</span>
                                            <span style={{
                                                color: dashboardColors.success,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.medianImprovement > 0 ? `${usecaseData.medianImprovement.toFixed(1)}% faster` : 'No improvement'}</span>
                                        </>
                                    )}

                                    {selectedMetric === 'min' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Min:</span>
                                            <span style={{
                                                color: dashboardColors.success,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.minImprovement > 0 ? `${usecaseData.minImprovement.toFixed(1)}% faster` : 'No improvement'}</span>
                                        </>
                                    )}

                                    {selectedMetric === 'max' && (
                                        <>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Max:</span>
                                            <span style={{
                                                color: dashboardColors.success,
                                                fontWeight: 'bold'
                                            }}>{usecaseData.maxImprovement > 0 ? `${usecaseData.maxImprovement.toFixed(1)}% faster` : 'No improvement'}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
            return null;
        };

        return (
            <Card
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Space>
                            <ClockCircleOutlined />
                            <span>Response Time Performance</span>
                            <Tooltip title="Response time comparison">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </Space>
                        {comparisonData && comparisonData.some(item => item.hasOptune && item.hasCommercial) && (
                            <Button
                                type={compareModels ? "primary" : "default"}
                                size="small"
                                icon={<span style={{ marginRight: '4px' }}>{compareModels ? "✓" : "⚡"}</span>}
                                onClick={() => setCompareModels(!compareModels)}
                                className="optune-compare-btn"
                                style={{
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    boxShadow: compareModels ? '0 0 10px rgba(24, 144, 255, 0.5)' : 'none'
                                }}
                            >
                                {compareModels ? "Comparing Models" : "Compare Models"}
                            </Button>
                        )}
                    </div>
                }
                className="dashboard-card"
            >
                {!data ? (
                    <CustomEmptyState
                        icon={<ClockCircleOutlined />}
                        title="Response Time Metrics"
                        description="No response time data available for current selection."
                        suggestion="Performance metrics will appear here when you select a specific usecase or application."
                    />
                ) : (!comparisonData || (
                    !compareModels && (!data.response_time || data.response_time.length === 0))
                ) ? (
                    <EmptyDataMessage
                        icon={<ClockCircleOutlined />}
                        message="No response time data to display"
                        height="100%"
                    />
                ) : compareModels && comparisonData && comparisonData.some(item => item.hasOptune && item.hasCommercial) ? (
                    // Comparison mode visualization
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>

                        Model Legend
                        <div className="optune-model-legend fade-in delay-1">
                            <div className="optune-model-label">
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: dashboardColors.success,
                                    borderRadius: '4px'
                                }}></div>
                                <span>Optune Models</span>
                            </div>
                            <div className="optune-model-label">
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: dashboardColors.info,
                                    borderRadius: '4px'
                                }}></div>
                                <span>Commercial Models</span>
                            </div>

                            <div style={{ marginLeft: 'auto' }}>
                                <Radio.Group
                                    value={selectedMetric}
                                    onChange={(e) => setSelectedMetric(e.target.value)}
                                    size="small"
                                    buttonStyle="solid"
                                    className="metric-selector"
                                >
                                    <Radio.Button value="avg">Average</Radio.Button>
                                    <Radio.Button value="median">Median</Radio.Button>
                                    <Radio.Button value="min">Min</Radio.Button>
                                    <Radio.Button value="max">Max</Radio.Button>
                                </Radio.Group>
                            </div>
                        </div>

                        {/* Performance Summary */}
                        {comparisonData.some(item => item.hasOptune && item.hasCommercial && item.improvementPercent > 0) && (
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '12px',
                                marginTop: '8px'
                            }}>
                                {comparisonData
                                    .filter(item => item.hasOptune && item.hasCommercial && item.improvementPercent > 0)
                                    .sort((a, b) => {
                                        // Sort by the selected metric's improvement
                                        const metricMap = {
                                            'avg': 'improvementPercent',
                                            'median': 'medianImprovement',
                                            'min': 'minImprovement',
                                            'max': 'maxImprovement'
                                        };
                                        const metric = metricMap[selectedMetric];
                                        return b[metric] - a[metric];
                                    })
                                    .slice(0, 3)
                                    .map((item, index) => {
                                        // Get current metric improvement
                                        const metricMap = {
                                            'avg': { improvement: 'improvementPercent', optuneVal: 'optuneAvg', commercialVal: 'commercialAvg' },
                                            'median': { improvement: 'medianImprovement', optuneVal: 'optuneMedian', commercialVal: 'commercialMedian' },
                                            'min': { improvement: 'minImprovement', optuneVal: 'optuneMin', commercialVal: 'commercialMin' },
                                            'max': { improvement: 'maxImprovement', optuneVal: 'optuneMax', commercialVal: 'commercialMax' }
                                        };

                                        const { improvement, optuneVal, commercialVal } = metricMap[selectedMetric];
                                        const improvementValue = item[improvement];
                                        const optuneValue = item[optuneVal];
                                        const commercialValue = item[commercialVal];

                                        return (
                                            <div key={`improvement-${index}`} className={`optune-improvement-card fade-in delay-${index + 1}`}>
                                                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.65)', marginBottom: '4px' }}>
                                                    {item.usecase}
                                                </div>
                                                <div style={{
                                                    fontSize: '18px',
                                                    fontWeight: 'bold',
                                                    color: dashboardColors.success,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}>
                                                    <RiseOutlined />
                                                    {improvementValue.toFixed(0)}% Faster with Optune
                                                </div>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: 'rgba(255, 255, 255, 0.85)',
                                                    marginTop: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}>
                                                    <span style={{ color: dashboardColors.info }}>{commercialValue.toFixed(1)}ms</span>
                                                    <span style={{ fontSize: '16px' }}>→</span>
                                                    <span style={{ color: dashboardColors.success }}>{optuneValue.toFixed(1)}ms</span>
                                                </div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        )}

                        {/* Chart */}
                        <div style={{ flex: 1, minHeight: '300px' }}>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={chartData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                    barGap={4}
                                    barCategoryGap="20%"
                                >
                                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                                    <XAxis
                                        dataKey="usecase"
                                        tick={{ fill: '#fff', fontSize: 12 }}
                                        axisLine={{ stroke: '#666' }}
                                        tickLine={{ stroke: '#666' }}
                                    />
                                    <YAxis
                                        unit=" ms"
                                        tick={{ fill: '#fff', fontSize: 12 }}
                                        axisLine={{ stroke: '#666' }}
                                        tickLine={{ stroke: '#666' }}
                                    />
                                    <RechartsTooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar
                                        dataKey="optune"
                                        name="Optune"
                                        fill={dashboardColors.success}
                                        radius={[3, 3, 0, 0]}
                                        animationDuration={1500}
                                        animationEasing="ease-out"
                                    />
                                    <Bar
                                        dataKey="commercial"
                                        name="Commercial"
                                        fill={dashboardColors.info}
                                        radius={[3, 3, 0, 0]}
                                        animationDuration={1500}
                                        animationEasing="ease-out"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                ) : (
                    // Non-comparison mode - show all metrics in a grouped bar chart for each usecase
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: '16px', overflow: 'auto' }}>
                            <div style={{ flex: '1 0 auto', minWidth: '120px' }}>
                                <Statistic
                                    title="Avg."
                                    value={(() => {
                                        if (!data.response_time || data.response_time.length === 0) return '0.00';
                                        const avg = data.response_time.reduce((sum, item) => sum + item.avg, 0) / data.response_time.length;
                                        return isNaN(avg) ? '0.00' : avg.toFixed(2);
                                    })()}
                                    suffix="ms"
                                    valueStyle={{ color: predictionColors[0] }}
                                />
                            </div>
                            <div style={{ flex: '1 0 auto', minWidth: '120px' }}>
                                <Statistic
                                    title="Median"
                                    value={(() => {
                                        if (!data.response_time || data.response_time.length === 0) return '0.00';
                                        const median = data.response_time.reduce((sum, item) => sum + item.median, 0) / data.response_time.length;
                                        return isNaN(median) ? '0.00' : median.toFixed(2);
                                    })()}
                                    suffix="ms"
                                    valueStyle={{ color: predictionColors[1] }}
                                />
                            </div>
                            <div style={{ flex: '1 0 auto', minWidth: '120px' }}>
                                <Statistic
                                    title="Min."
                                    value={(() => {
                                        if (!data.response_time || data.response_time.length === 0) return '0.00';
                                        const min = Math.min(...data.response_time.map(item => item.min));
                                        return isNaN(min) || min === Infinity ? '0.00' : min.toFixed(2);
                                    })()}
                                    suffix="ms"
                                    valueStyle={{ color: predictionColors[2] }}
                                />
                            </div>
                            <div style={{ flex: '1 0 auto', minWidth: '120px' }}>
                                <Statistic
                                    title="Max."
                                    value={(() => {
                                        if (!data.response_time || data.response_time.length === 0) return '0.00';
                                        const max = Math.max(...data.response_time.map(item => item.max));
                                        return isNaN(max) || max === -Infinity ? '0.00' : max.toFixed(2);
                                    })()}
                                    suffix="ms"
                                    valueStyle={{ color: predictionColors[3] }}
                                />
                            </div>
                        </div>

                        {/* Metric Legend for non-comparison mode */}
                        <div className="optune-model-legend fade-in delay-1">
                            <div className="optune-model-label">
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: predictionColors[0],
                                    borderRadius: '4px'
                                }}></div>
                                <span>Average</span>
                            </div>
                            <div className="optune-model-label">
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: predictionColors[1],
                                    borderRadius: '4px'
                                }}></div>
                                <span>Median</span>
                            </div>
                            <div className="optune-model-label">
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: predictionColors[2],
                                    borderRadius: '4px'
                                }}></div>
                                <span>Min</span>
                            </div>
                            <div className="optune-model-label">
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: predictionColors[3],
                                    borderRadius: '4px'
                                }}></div>
                                <span>Max</span>
                            </div>
                        </div>

                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                                data={data?.response_time?.map(item => ({
                                    ...item,
                                    // Ensuring all metrics are properly captured
                                    avg: item.avg || 0,
                                    median: item.median || 0,
                                    min: item.min || (item.avg ? item.avg * 0.8 : 0), // Fallback if missing
                                    max: item.max || (item.avg ? item.avg * 1.2 : 0), // Fallback if missing
                                }))}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                barGap={2}
                                barCategoryGap="15%"
                            >
                                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                                <XAxis
                                    dataKey="usecase"
                                    tick={{ fill: '#fff', fontSize: 12 }}
                                    axisLine={{ stroke: '#666' }}
                                    tickLine={{ stroke: '#666' }}
                                />
                                <YAxis
                                    unit=" ms"
                                    tick={{ fill: '#fff', fontSize: 12 }}
                                    axisLine={{ stroke: '#666' }}
                                    tickLine={{ stroke: '#666' }}
                                />
                                <RechartsTooltip
                                    formatter={(value, name) => [`${value.toFixed(2)} ms`, name.charAt(0).toUpperCase() + name.slice(1)]}
                                    contentStyle={{
                                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                                        border: '1px solid #444',
                                        borderRadius: '4px',
                                        padding: '10px',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                    }}
                                    labelStyle={{ color: '#fff', fontWeight: 'bold', marginBottom: '8px' }}
                                    itemStyle={{ color: '#fff' }}
                                />
                                <Legend />
                                <Bar
                                    dataKey="avg"
                                    name="Average"
                                    fill={predictionColors[0]}
                                    radius={[3, 3, 0, 0]}
                                    animationDuration={1000 + Math.random() * 500}
                                    animationEasing="ease-out"
                                />
                                <Bar
                                    dataKey="median"
                                    name="Median"
                                    fill={predictionColors[1]}
                                    radius={[3, 3, 0, 0]}
                                    animationDuration={1000 + Math.random() * 500}
                                    animationEasing="ease-out"
                                />
                                <Bar
                                    dataKey="min"
                                    name="Min"
                                    fill={predictionColors[2]}
                                    radius={[3, 3, 0, 0]}
                                    animationDuration={1000 + Math.random() * 500}
                                    animationEasing="ease-out"
                                />
                                <Bar
                                    dataKey="max"
                                    name="Max"
                                    fill={predictionColors[3]}
                                    radius={[3, 3, 0, 0]}
                                    animationDuration={1000 + Math.random() * 500}
                                    animationEasing="ease-out"
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )
                }
            </Card >
        );
    };

    const renderPredictionDistributionCard = () => {
        // Calculate total predictions and percentages
        const totalPredictions = data?.prediction_distribution?.reduce((sum, item) => sum + item.count, 0) || 0;

        // Use the fetched display names instead of building from raw_requests
        const currentDisplayNames = displayNames[selectedUsecase] || {};

        // Consolidate distribution data to avoid duplicate entries for the same label
        const consolidatedDistribution = {};
        data?.prediction_distribution?.forEach(item => {
            const label = item.label;
            if (consolidatedDistribution[label]) {
                consolidatedDistribution[label].count += item.count;
            } else {
                consolidatedDistribution[label] = {
                    label,
                    count: item.count,
                    display_name: currentDisplayNames[label] || null
                };
            }
        });

        // Convert consolidated object to array and add percentage
        const distributionWithPercentage = Object.values(consolidatedDistribution).map(item => ({
            ...item,
            percentage: ((item.count / totalPredictions) * 100).toFixed(1)
        }));

        // Find most common prediction
        const mostCommonPrediction = [...distributionWithPercentage]
            .sort((a, b) => b.count - a.count)[0] || { label: 'N/A', count: 0, display_name: null };

        return (
            <Card
                title={
                    <Space>
                        <PieChartOutlined />
                        <span>Prediction Distribution</span>
                        <Tooltip title="Distribution of prediction labels returned by models">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Space>
                }
                className="dashboard-card prediction-distribution-card"
            >
                {!data ? (
                    <CustomEmptyState
                        icon={<DatabaseOutlined />}
                        title="Prediction Label Analysis"
                        description="No prediction data available for the current selection."
                        suggestion="Select a specific usecase to see the distribution of prediction labels."
                    />
                ) : (!data.prediction_distribution || data.prediction_distribution.length === 0) ? (
                    <EmptyDataMessage
                        icon={<DatabaseOutlined />}
                        message="No prediction distribution data for current selection"
                    />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Summary statistics */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            flexWrap: 'nowrap',
                            gap: '16px',
                            overflow: 'auto',
                            marginBottom: '8px'
                        }}>
                            <div style={{ flex: '1 0 auto', minWidth: '120px' }}>
                                <Statistic
                                    title="Total Predictions"
                                    value={totalPredictions}
                                    valueStyle={{
                                        color: dashboardColors.info,
                                        fontSize: '24px'
                                    }}
                                />
                            </div>
                            <div style={{ flex: '1 0 auto', minWidth: '120px' }}>
                                <Statistic
                                    title="Unique Labels"
                                    value={Object.keys(consolidatedDistribution).length || 0}
                                    valueStyle={{
                                        color: dashboardColors.cyan,
                                        fontSize: '24px'
                                    }}
                                />
                            </div>
                            <div style={{ flex: '1 0 auto', minWidth: '160px' }}>
                                <Statistic
                                    title="Most Common"
                                    value={mostCommonPrediction.label}
                                    suffix={
                                        <span style={{
                                            fontSize: '14px',
                                            color: 'rgba(255,255,255,0.65)',
                                            marginLeft: '8px'
                                        }}>
                                            ({((mostCommonPrediction.count / totalPredictions) * 100).toFixed(1)}%)
                                        </span>
                                    }
                                    valueStyle={{
                                        color: getPredictionColor(mostCommonPrediction.label),
                                        fontSize: '24px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                />
                                {mostCommonPrediction.display_name && (
                                    <div style={{
                                        background: `linear-gradient(135deg, ${getPredictionColor(mostCommonPrediction.label)} 0%, ${getPredictionColor(mostCommonPrediction.label)}dd 100%)`,
                                        fontSize: '13px',
                                        padding: '2px 10px',
                                        borderRadius: '12px',
                                        boxShadow: `0 2px 6px ${getPredictionColor(mostCommonPrediction.label)}50`,
                                        fontWeight: 'bold',
                                        lineHeight: '1.2',
                                        display: 'inline-block',
                                        marginTop: '4px'
                                    }}>
                                        {mostCommonPrediction.display_name}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Chart and legend container */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '20px',
                            height: '300px'
                        }}>
                            {/* Chart */}
                            <div style={{ flex: '1 1 500px', minWidth: '0', minHeight: '300px' }}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart
                                        data={distributionWithPercentage}
                                        margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
                                        layout="vertical"
                                    >
                                        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                                        <XAxis type="number" />
                                        <YAxis
                                            dataKey="label"
                                            type="category"
                                            width={150}
                                            tick={(props) => {
                                                const { x, y, payload } = props;
                                                const item = distributionWithPercentage.find(i => i.label === payload.value);
                                                const hasDisplayName = item && item.display_name && item.display_name !== payload.value;

                                                return (
                                                    <g transform={`translate(${x},${y})`}>
                                                        <text x={-6} y={0} dy={4} textAnchor="end" fontSize={12}>
                                                            {payload.value.length > 14 ? `${payload.value.substring(0, 14)}...` : payload.value} {hasDisplayName && (`(${item.display_name})`)}
                                                        </text>
                                                    </g>
                                                );
                                            }}
                                        />
                                        <RechartsTooltip
                                            formatter={(value, name, props) => {
                                                const item = props.payload;
                                                return [
                                                    <span key={`value-${name}`} style={{ display: 'flex', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
                                                        <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#fff' }}>
                                                            {value.toLocaleString()}
                                                        </span>
                                                        <span style={{ fontSize: '14px', color: '#7CB5EC' }}>
                                                            ({item?.percentage || '0.0'}%)
                                                        </span>
                                                    </span>,
                                                    <span key={`label-${name}`} style={{ color: '#999' }}>Count</span>
                                                ];
                                            }}
                                            labelFormatter={(label) => {
                                                const item = distributionWithPercentage.find(i => i.label === label);
                                                const hasDisplayName = item && item.display_name && item.display_name !== label;
                                                const color = getPredictionColor(label);

                                                return (
                                                    <span style={{
                                                        color: '#fff',
                                                        fontSize: '14px',
                                                        padding: '4px 0',
                                                        borderBottom: '1px solid rgba(255,255,255,0.2)',
                                                        marginBottom: '6px',
                                                        display: 'block'
                                                    }}>
                                                        Prediction: <strong>{label}</strong>
                                                        {hasDisplayName && (
                                                            <span style={{
                                                                display: 'inline-block',
                                                                background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                                                                color: 'black',
                                                                fontSize: '13px',
                                                                padding: '2px 10px',
                                                                borderRadius: '12px',
                                                                boxShadow: `0 2px 6px ${color}50`,
                                                                fontWeight: 'bold',
                                                                lineHeight: '1.2',
                                                                marginLeft: '8px'
                                                            }}>
                                                                {item.display_name}
                                                            </span>
                                                        )}
                                                    </span>
                                                );
                                            }}
                                            contentStyle={{
                                                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                                                border: '1px solid #444',
                                                borderRadius: '4px',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                                padding: '10px 14px'
                                            }}
                                            labelStyle={{ fontWeight: 'bold', marginBottom: '5px' }}
                                        />
                                        <Bar
                                            dataKey="count"
                                            name="Count"
                                            animationDuration={1500}
                                            animationEasing="ease-out"
                                        >
                                            {distributionWithPercentage.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={dashboardColors.series[index % dashboardColors.series.length]}
                                                    style={{ cursor: 'pointer' }}
                                                    stroke="#fff"
                                                    strokeWidth={0.5}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                )}
            </Card>
        );
    };

    // Add handler for pagination and filter changes
    const handlePaginationChange = (page, size) => {
        // Update state with the exact values from the UI
        setCurrentPage(page);
        setPageSize(size);

        // Use our top-level tableFilters state
        // Refetch data with new pagination settings and the current filters
        const filters = {
            application: selectedApp !== "All Projects" ? selectedApp : null,
            usecase: selectedUsecase !== "All Tasks" ? selectedUsecase : null,
            date_range: dateRange ? [
                dateRange[0].format('YYYY-MM-DD'),
                dateRange[1].format('YYYY-MM-DD')
            ] : null,
            // Use the page parameter directly to ensure consistency
            page: page,
            page_size: size,
            // Add the current top-level filters
            ...tableFilters
        };

        // Pass the filters object directly to fetchMetrics
        fetchMetrics(filters);
    };

    // Handle all table changes including sorting
    const handleTableChange = (pagination, filters, sorter) => {
        // Update pagination state
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);

        // Prepare sort parameters if sorter is provided
        let sortParams = {};
        if (sorter && sorter.field) {
            // Use sortField from column definition if available, otherwise use the field name
            const sortField = sorter.column?.sortField || sorter.field;

            // Only include sort parameters if there's a valid order
            if (sorter.order) {
                sortParams = {
                    sort_field: sortField,
                    sort_order: sorter.order === 'ascend' ? 'asc' : 'desc'
                };
            }
        }

        // First, create the base filter params - use "All Projects"/"All Tasks" instead of null
        const filterParams = {
            application: selectedApp !== "All Projects" ? selectedApp : "All Projects",
            usecase: selectedUsecase !== "All Tasks" ? selectedUsecase : "All Tasks",
            date_range: dateRange ? [
                dateRange[0].format('YYYY-MM-DD'),
                dateRange[1].format('YYYY-MM-DD')
            ] : null,
            page: pagination.current,
            page_size: pagination.pageSize,
            ...tableFilters,
            ...sortParams
        };

        // Fetch data with updated parameters
        fetchMetrics(filterParams);
    };

    // Make sure this function exists to render raw data table
    const renderRawDataTable = (allLabels) => {
        return (
            <Card
                title={
                    <Space>
                        <TableOutlined />
                        <span>Raw Request Data</span>
                        <Tooltip title="Individual request details">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Space>
                }
                className="dashboard-card"
            >
                {!data || (data?.raw_requests?.length === 0) ? (
                    <CustomEmptyState
                        icon={<SearchOutlined />}
                        title="No Request Data Found"
                        description="There are no requests matching your current filter criteria."
                        suggestion="Try adjusting your filters or selecting a different time range."
                        action={{
                            label: "Clear Filters",
                            onClick: () => {
                                // Clear time range filters
                                setSelectedApp('All Projects');
                                setSelectedUsecase('All Tasks');
                                setDateRange(null);
                                setTableFilters({});

                                // Also clear table filters if the ref is available
                                if (rawDataTableRef.current) {
                                    rawDataTableRef.current.clearFilters();
                                } else {
                                    // If table ref isn't available, at least fetch fresh data
                                    fetchMetrics({});
                                }
                            }
                        }}
                        height={400}
                    />
                ) : (
                    <>
                        <RawDataTable
                            ref={rawDataTableRef}
                            data={data?.raw_requests}
                            loading={loading}
                            totalRecords={totalRecords}
                            onPaginationChange={handlePaginationChange}
                            onTableChange={handleTableChange}
                            onClearAllFilters={() => {
                                // When table's clear filters is called, also clear dashboard filters
                                setSelectedApp('All Projects');
                                setSelectedUsecase('All Tasks');
                                setDateRange(null);
                                setTableFilters({});
                            }}
                            allLabels={allLabels}
                        />
                    </>
                )}
            </Card>
        );
    };

    const handleFiltersChange = (newFilters) => {
        // Update table filters state
        setTableFilters(newFilters);
        // Refetch data with new filters
        fetchMetrics({
            application: selectedApp !== "All Projects" ? selectedApp : null,
            usecase: selectedUsecase !== "All Tasks" ? selectedUsecase : null,
            date_range: dateRange ? [
                dateRange[0].format('YYYY-MM-DD'),
                dateRange[1].format('YYYY-MM-DD')
            ] : null,
            page: currentPage,
            page_size: pageSize,
            ...newFilters  // Include the new filters directly instead of using tableFilters state
        });
    };

    return (
        <Layout className="deployment-monitoring page-content-shell">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginLeft: '10px' }}><SparkText variant="h2" style={{ fontSize: '30px', fontWeight: 350, whiteSpace: 'nowrap' }}>Deployment Monitoring</SparkText></div>
            <Content style={{ paddingTop: '10px', paddingBottom: '10px', minHeight: 'calc(100vh - 64px)' }}>
                {/* Filters */}
                <Card style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                            <div style={{ flex: '1 1 250px', minWidth: '0' }}>
                                <Text strong>Project:</Text>
                                <Select
                                    placeholder="Select Project"
                                    style={{ width: '100%', marginTop: '8px' }}
                                    value={selectedApp}
                                    onChange={handleAppChange}
                                    loading={applications.length === 0}
                                >
                                    {applications?.map(app => (
                                        <Option key={app} value={app}>{app}</Option>
                                    ))}
                                </Select>
                            </div>
                            <div style={{ flex: '1 1 250px', minWidth: '0' }}>
                                <Text strong>Task:</Text>
                                <Select
                                    placeholder="Select Task"
                                    style={{ width: '100%', marginTop: '8px' }}
                                    value={selectedUsecase}
                                    onChange={handleUsecaseChange}
                                    loading={usecases?.length === 0}
                                >
                                    {usecases?.map(usecase => (
                                        <Option key={usecase} value={usecase}>{usecase}</Option>
                                    ))}
                                </Select>
                            </div>
                            <div style={{ flex: '1 1 300px', minWidth: '0' }}>
                                <Text strong>Time Range:</Text>
                                <div style={{ position: 'relative' }}>
                                    <RangePicker
                                        style={{ width: '100%', marginTop: '8px' }}
                                        onChange={handleDateRangeChange}
                                        allowClear
                                        placeholder={['Start Date', 'End Date']}
                                        value={dateRange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Advanced Filters Section */}
                        <div style={{
                            marginTop: '16px',
                            borderTop: '1px dashed rgba(255, 255, 255, 0.15)',
                            paddingTop: '16px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <Text strong style={{ fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                                    <FilterOutlined style={{ marginRight: '8px' }} />
                                    Advanced Filters
                                    <Tooltip title="These filters affect all data displayed on this dashboard">
                                        <QuestionCircleOutlined style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.7 }} />
                                    </Tooltip>
                                </Text>
                                {Object.keys(tableFilters).length > 0 && (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<ClearOutlined />}
                                        onClick={() => {
                                            setTableFilters({});
                                            // If table ref is available, also clear filters there
                                            if (rawDataTableRef.current) {
                                                rawDataTableRef.current.clearFilters();
                                            }
                                            // Refetch data without table filters
                                            fetchMetrics({
                                                application: selectedApp !== "All Projects" ? selectedApp : null,
                                                usecase: selectedUsecase !== "All Tasks" ? selectedUsecase : null,
                                                date_range: dateRange ? [
                                                    dateRange[0].format('YYYY-MM-DD'),
                                                    dateRange[1].format('YYYY-MM-DD')
                                                ] : null,
                                                page: currentPage,
                                                page_size: pageSize
                                                // No filters here since we're clearing them
                                            });
                                        }}
                                    >
                                        Clear Filters
                                    </Button>
                                )}
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                {/* Model Filter */}
                                <div style={{ flex: '1 1 200px', minWidth: '0' }}>
                                    <Text>Model:</Text>
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        placeholder="Select Models"
                                        style={{ width: '100%', marginTop: '8px' }}
                                        value={tableFilters.models || []}
                                        onChange={(values) => {
                                            const newFilters = { ...tableFilters };
                                            if (values.length > 0) {
                                                newFilters.models = values;
                                            } else {
                                                delete newFilters.models;
                                            }
                                            setTableFilters(newFilters);
                                            handleFiltersChange(newFilters);
                                        }}
                                        options={[
                                            { label: 'Optune', value: 'Optune' },
                                            ...[...new Set(data?.raw_requests?.filter(item => !item.isFromOptuneModel && item.modelname).map(item => item.modelname))]
                                                .map(model => ({ label: model, value: model }))
                                        ]}
                                    />
                                </div>

                                {/* Status Filter */}
                                <div style={{ flex: '1 1 200px', minWidth: '0' }}>
                                    <Text>Status:</Text>
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        placeholder="Filter by Status"
                                        style={{ width: '100%', marginTop: '8px' }}
                                        value={tableFilters.status || []}
                                        onChange={(values) => {
                                            const newFilters = { ...tableFilters };
                                            if (values.length > 0) {
                                                newFilters.status = values;
                                            } else {
                                                delete newFilters.status;
                                            }
                                            setTableFilters(newFilters);
                                            handleFiltersChange(newFilters);
                                        }}
                                        options={[
                                            { label: 'Success', value: 'success' },
                                            { label: 'Failed', value: 'failed' }
                                        ]}
                                    />
                                </div>

                                {/* Prediction Filter - only show if we have prediction data */}
                                {data?.prediction_distribution && data.prediction_distribution.length > 0 && (
                                    <div style={{ flex: '1 1 200px', minWidth: '0' }}>
                                        <Text>Prediction:</Text>
                                        <Select
                                            mode="multiple"
                                            allowClear
                                            placeholder="Filter by Prediction"
                                            style={{ width: '100%', marginTop: '8px' }}
                                            value={tableFilters.predictions || []}
                                            onChange={(values) => {
                                                const newFilters = { ...tableFilters };
                                                if (values.length > 0) {
                                                    newFilters.predictions = values;
                                                } else {
                                                    delete newFilters.predictions;
                                                }
                                                setTableFilters(newFilters);
                                                handleFiltersChange(newFilters);
                                            }}
                                            options={[...new Set(data?.raw_requests?.map(req => req.prediction).filter(Boolean))]
                                                .map(pred => ({
                                                    label: pred,
                                                    value: pred
                                                }))}
                                            optionRender={(option) => (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}>
                                                    <div style={{
                                                        width: '12px',
                                                        height: '12px',
                                                        borderRadius: '50%',
                                                        backgroundColor: getPredictionColor(option.value)
                                                    }} />
                                                    {option.label}
                                                </div>
                                            )}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Active Filter Tags */}
                            {Object.keys(tableFilters).length > 0 && (
                                <div style={{
                                    marginTop: '16px',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px'
                                }}>
                                    {tableFilters.models && tableFilters.models.map(model => (
                                        <Tag
                                            key={`model-${model}`}
                                            closable
                                            color="blue"
                                            onClose={() => {
                                                const newFilters = { ...tableFilters };
                                                newFilters.models = newFilters.models.filter(m => m !== model);
                                                if (newFilters.models.length === 0) {
                                                    delete newFilters.models;
                                                }
                                                setTableFilters(newFilters);
                                                handleFiltersChange(newFilters);
                                            }}
                                        >
                                            Model: {model}
                                        </Tag>
                                    ))}

                                    {tableFilters.status && tableFilters.status.map(status => (
                                        <Tag
                                            key={`status-${status}`}
                                            closable
                                            color={status === 'success' ? 'success' : 'error'}
                                            onClose={() => {
                                                const newFilters = { ...tableFilters };
                                                newFilters.status = newFilters.status.filter(s => s !== status);
                                                if (newFilters.status.length === 0) {
                                                    delete newFilters.status;
                                                }
                                                setTableFilters(newFilters);
                                                handleFiltersChange(newFilters);
                                            }}
                                        >
                                            Status: {status === 'success' ? 'Success' : 'Failed'}
                                        </Tag>
                                    ))}

                                    {tableFilters.predictions && tableFilters.predictions.map(prediction => (
                                        <Tag
                                            key={`prediction-${prediction}`}
                                            closable
                                            color={getPredictionColor(prediction, true)}
                                            onClose={() => {
                                                const newFilters = { ...tableFilters };
                                                newFilters.predictions = newFilters.predictions.filter(p => p !== prediction);
                                                if (newFilters.predictions.length === 0) {
                                                    delete newFilters.predictions;
                                                }
                                                setTableFilters(newFilters);
                                                handleFiltersChange(newFilters);
                                            }}
                                        >
                                            Prediction: {prediction}
                                        </Tag>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Requests Found Counter - Clean and Compact Style */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: '10px'
                        }}>
                            <div>
                                <Text style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.85)' }}>
                                    <DatabaseOutlined style={{ marginRight: '6px', fontSize: '12px', opacity: 0.7 }} />
                                    <span style={{ fontWeight: 'bold' }}>{totalRecords}</span>
                                    <span style={{ marginLeft: '4px', opacity: 0.7 }}>requests found</span>
                                    <Tooltip title={`Showing data for ${dateRange
                                        ? `${dateRange[0].format('MMM D, YYYY')} - ${dateRange[1].format('MMM D, YYYY')}`
                                        : 'All Time'}`}>
                                        <span style={{
                                            marginLeft: '8px',
                                            background: 'rgba(0, 0, 0, 0.2)',
                                            padding: '2px 8px',
                                            borderRadius: '10px',
                                            fontSize: '11px',
                                            border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }}>
                                            {dateRange
                                                ? `${dateRange[0].format('MMM D, YYYY')} - ${dateRange[1].format('MMM D, YYYY')}`
                                                : 'All Time'}
                                        </span>
                                    </Tooltip>
                                </Text>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Summary Statistics */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '24px',
                    justifyContent: 'space-between',
                    marginBottom: "24px",
                }}>
                    {/* Total Requests */}
                    <Card
                        style={{
                            flex: '1',
                            minWidth: '200px',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        styles={{
                            body: {
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }
                        }}
                        title={<Space>
                            <BarChartOutlined />
                            <span>Total Requests</span>
                        </Space>}
                    >
                        {!data ? (
                            <StatisticPlaceholder
                                color={dashboardColors.info}
                                icon={<RiseOutlined />}
                                text="No data"
                            />
                        ) : (
                            <Statistic
                                value={data?.requests_per_usecase?.reduce((sum, item) => sum + item.requests, 0) || 0}
                                groupSeparator=","
                                valueStyle={{
                                    color: dashboardColors.info,
                                    textAlign: 'center',
                                    display: 'block',
                                    fontSize: "40px",
                                }}
                            />
                        )}
                    </Card>

                    {/* Success Rate */}
                    <Card
                        style={{
                            flex: '1',
                            minWidth: '200px',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        styles={{
                            body: {
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }
                        }}
                        title={
                            <Space>
                                <PieChartOutlined />
                                <span>Success Rate</span>
                            </Space>
                        }>
                        {!data ? (
                            <StatisticPlaceholder
                                color={dashboardColors.success}
                                icon={<PieChartOutlined />}
                                text="No data"
                            />
                        ) : (
                            <Statistic
                                value={(() => {
                                    const totalSuccess = data?.success_failure?.reduce((sum, item) => sum + item.success, 0) || 0;
                                    const totalFailure = data?.success_failure?.reduce((sum, item) => sum + item.failure, 0) || 0;
                                    const totalRequests = totalSuccess + totalFailure;
                                    return totalRequests > 0 ? ((totalSuccess / totalRequests) * 100).toFixed(2) : '0.00';
                                })()}
                                suffix="%"
                                valueStyle={{
                                    color: dashboardColors.success,
                                    fontSize: '32px',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap'
                                }}
                            />
                        )}
                    </Card>

                    {/* Average Response Time */}
                    <Card
                        style={{
                            flex: '1',
                            minWidth: '200px',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        styles={{
                            body: {
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }
                        }}
                        title={
                            <Space>
                                <ClockCircleOutlined />
                                <span>Average Response Time</span>
                            </Space>
                        }>
                        {!data ? (
                            <StatisticPlaceholder
                                color={dashboardColors.purple}
                                icon={<ClockCircleOutlined />}
                                text="No data"
                            />
                        ) : (
                            <Statistic
                                value={(() => {
                                    if (!data.response_time || data.response_time.length === 0) return '0.00';
                                    const avg = data.response_time.reduce((sum, item) => sum + item.avg, 0) / data.response_time.length;
                                    return isNaN(avg) ? '0.00' : avg.toFixed(2);
                                })()}
                                suffix="ms"
                                valueStyle={{
                                    color: dashboardColors.purple,
                                    textAlign: 'center',
                                    display: 'block',
                                    fontSize: "40px",
                                }}
                            />
                        )}
                    </Card>
                </div>

                {/* Main content */}
                <>
                    {/* Top Level Stats */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                        <div style={{ flex: '1 1 400px', minWidth: '0' }}>
                            {renderUsecaseRequestsCard()}
                        </div>
                        <div style={{ flex: '1 1 400px', minWidth: '0' }}>
                            {renderSuccessFailureCard()}
                        </div>
                    </div>
                    <div style={{ marginTop: '16px' }}>
                        {renderDailyRequestsCard()}
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '16px' }}>
                        <div style={{ flex: '1 1 400px', minWidth: '0' }}>
                            {renderResponseTimeCard()}
                        </div>
                        {selectedUsecase !== "All Tasks" && <div style={{ flex: '1 1 400px', minWidth: '0' }}>
                            {renderPredictionDistributionCard()}
                        </div>}
                    </div>

                    {/* Raw Data Table */}
                    <div style={{ marginTop: '16px' }}>
                        {renderRawDataTable([])}
                    </div>
                </>
            </Content>
        </Layout>
    );
};

// Dark theme wrapper component
const ProductionMonitoringPage = () => (
    <ConfigProvider>
        <ProductionMonitoring />
    </ConfigProvider>
);

export default ProductionMonitoringPage;